import type {
  GeoJSONSourceSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';

import type { BarrierDraft } from '@/stores/BarrierWorkspaceContext';

import { BARRIER_ICON_IMAGE_ID } from './barrierIcon';
import { WAYPOINT_ICON_IMAGE_ID } from './waypointIcon';

export const DRAFT_SOURCE_ID = 'barrier-draft';
export const DRAFT_ICON_LAYER_ID = 'barrier-draft-icon';
export const DRAFT_HINT_LINE_LAYER_ID = 'barrier-draft-hint';
export const DRAFT_WAYPOINTS_LAYER_ID = 'barrier-draft-waypoints';

type DraftProperties = {
  readonly kind: 'center' | 'hint_end' | 'waypoint';
  readonly index?: number;
};

export function buildEmptyDraftCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

export function buildDraftCollection(draft: BarrierDraft | null): FeatureCollection {
  if (!draft) {
    return buildEmptyDraftCollection();
  }
  const features: Feature[] = [
    {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [draft.point.lon, draft.point.lat] },
      properties: { kind: 'center' } satisfies DraftProperties,
    },
  ];
  const hint = draft.direction_hint;
  if (hint && hint.length === 2) {
    const start = hint[0];
    const end = hint[1];
    if (start && end) {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [
            [start.lon, start.lat],
            [end.lon, end.lat],
          ],
        },
        properties: { kind: 'hint_end' } satisfies DraftProperties,
      });
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [end.lon, end.lat] },
        properties: { kind: 'hint_end' } satisfies DraftProperties,
      });
    }
  }
  draft.auto_waypoints.forEach((wp, index) => {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [wp.lon, wp.lat] },
      properties: { kind: 'waypoint', index } satisfies DraftProperties,
    });
  });
  return { type: 'FeatureCollection', features };
}

export function buildDraftSource(): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: buildEmptyDraftCollection(),
  };
}

export function buildDraftIconLayer(): SymbolLayerSpecification {
  return {
    id: DRAFT_ICON_LAYER_ID,
    type: 'symbol',
    source: DRAFT_SOURCE_ID,
    filter: ['==', ['get', 'kind'], 'center'],
    layout: {
      'icon-image': BARRIER_ICON_IMAGE_ID,
      'icon-size': 1.0,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 0.92,
    },
  };
}

export function buildDraftHintLineLayer(): LineLayerSpecification {
  return {
    id: DRAFT_HINT_LINE_LAYER_ID,
    type: 'line',
    source: DRAFT_SOURCE_ID,
    filter: ['==', ['geometry-type'], 'LineString'],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#1677ff',
      'line-width': 3,
      'line-opacity': 0.9,
      'line-dasharray': [2, 2],
    },
  };
}

export function buildDraftWaypointsLayer(): SymbolLayerSpecification {
  return {
    id: DRAFT_WAYPOINTS_LAYER_ID,
    type: 'symbol',
    source: DRAFT_SOURCE_ID,
    filter: ['==', ['get', 'kind'], 'waypoint'],
    layout: {
      'icon-image': WAYPOINT_ICON_IMAGE_ID,
      'icon-size': 1.0,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 0.95,
    },
  };
}
